import React, { useState, useEffect } from 'react';
import axios from './api/axios';
import TranslationConfigManager from './TranslationConfigManager';
import { SmartAssessmentCompact } from './components/SmartAssessment';
import PendingRequirementsPanel from './components/PendingRequirementsPanel';
import { getPendingCount } from './api/masterRequirementsApi';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Slider,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  CheckCircle,
  Psychology as PsychologyIcon,
  Visibility
} from '@mui/icons-material';

const MasterRequirementsManager = ({ setCurrentTab, setProcessingPanelOpen, currentProcessing, userPermissions }) => {
  // Obtener token de localStorage
  const [token] = useState(() => localStorage.getItem('token'));
  
  console.log('🎯 MasterRequirementsManager montado');
  console.log('🔑 Token disponible:', !!token);
  console.log('📍 Token length:', token?.length);
  console.log('🔐 User permissions:', userPermissions);
  
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [specCodeFilter, setSpecCodeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentRequirement, setCurrentRequirement] = useState(null);
  const [editedVersion, setEditedVersion] = useState('');
  const [editedNameTranslation, setEditedNameTranslation] = useState('');
  const [editedTextTranslation, setEditedTextTranslation] = useState('');
  const [isValidated, setIsValidated] = useState(false);
  
  // Estados para campos SMART
  const [smartSpecific, setSmartSpecific] = useState(0);
  const [smartMeasurable, setSmartMeasurable] = useState(0);
  const [smartAchievable, setSmartAchievable] = useState(0);
  const [smartRelevant, setSmartRelevant] = useState(0);
  const [smartTimeBound, setSmartTimeBound] = useState(0);
  const [smartOverallScore, setSmartOverallScore] = useState(0);
  const [smartSpecificReasoning, setSmartSpecificReasoning] = useState('');
  const [smartMeasurableReasoning, setSmartMeasurableReasoning] = useState('');
  const [smartAchievableReasoning, setSmartAchievableReasoning] = useState('');
  const [smartRelevantReasoning, setSmartRelevantReasoning] = useState('');
  const [smartTimeBoundReasoning, setSmartTimeBoundReasoning] = useState('');
  
  // Estados para override y justificaciones de usuario
  const [smartSpecificOverride, setSmartSpecificOverride] = useState(false);
  const [smartSpecificJustification, setSmartSpecificJustification] = useState('');
  const [smartMeasurableOverride, setSmartMeasurableOverride] = useState(false);
  const [smartMeasurableJustification, setSmartMeasurableJustification] = useState('');
  const [smartAchievableOverride, setSmartAchievableOverride] = useState(false);
  const [smartAchievableJustification, setSmartAchievableJustification] = useState('');
  const [smartRelevantOverride, setSmartRelevantOverride] = useState(false);
  const [smartRelevantJustification, setSmartRelevantJustification] = useState('');
  const [smartTimeBoundOverride, setSmartTimeBoundOverride] = useState(false);
  const [smartTimeBoundJustification, setSmartTimeBoundJustification] = useState('');
  const [smartAnalysisCompleted, setSmartAnalysisCompleted] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Sub-tabs: 0 = Master Requirements, 1 = Pending Validation
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const showSuccess = (msg) => setSuccess(msg);
  const showError = (msg) => setError(msg);
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Estados para configuración de traducción
  const [translationConfigs, setTranslationConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [showConfigManager, setShowConfigManager] = useState(false);

  // Estados para versiones
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);

  // Estados para modal de confirmación de descarga
  const [confirmDownloadOpen, setConfirmDownloadOpen] = useState(false);
  const [validatedCount, setValidatedCount] = useState(0);

  // Estados para modal de confirmación de eliminación
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [requirementToDelete, setRequirementToDelete] = useState(null);

  // Estados para borrar todos
  const [confirmDeleteAllOpen, setConfirmDeleteAllOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  // Estados para análisis SMART
  const [confirmSmartAnalysisOpen, setConfirmSmartAnalysisOpen] = useState(false);
  const [analyzingSmart, setAnalyzingSmart] = useState(false);
  const [smartAnalysisInfo, setSmartAnalysisInfo] = useState(null);

  // Cargar requerimientos
  const loadRequirements = async (silent = false) => {
    // Si es silencioso (refresh automático), usar backgroundLoading
    // Si no hay datos previos o es carga explícita, mostrar loading normal
    if (silent && requirements.length > 0) {
      setBackgroundLoading(true);
    } else {
      setLoading(true);
    }
    setError('');
    
    try {
      const response = await axios.get('/master-requirements/', {
        params: {
          page: page + 1, // Backend usa páginas basadas en 1
          page_size: rowsPerPage,
          spec_code: specCodeFilter,
          search: searchTerm
        },
        timeout: 60000
      });

      console.log('✅ Respuesta del servidor:', response.data);
      console.log('Total de requisitos recibidos:', response.data.data?.requirements?.length || 0);
      console.log('Total count:', response.data.data?.pagination?.total_count || 0);

      if (response.data.status === 'success') {
        setRequirements(response.data.data.requirements);
        setTotalCount(response.data.data.pagination.total_count);
        
        // Contar requisitos validados
        const validated = response.data.data.requirements.filter(req => req.is_validated).length;
        setValidatedCount(validated);
        
        console.log('📊 Estado actualizado:', {
          totalRequirements: response.data.data.requirements.length,
          totalCount: response.data.data.pagination.total_count,
          validatedCount: validated
        });
      } else {
        console.warn('⚠️ Respuesta sin status success:', response.data);
      }
    } catch (err) {
      console.error('❌ Error al cargar requisitos:', err);
      console.error('Response data:', err.response?.data);
      console.error('Status:', err.response?.status);
      console.error('URL completa:', err.config?.url);
      setError('Error loading requirements: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      setBackgroundLoading(false);
    }
  };

  // Cargar requisitos al montar el componente y cuando cambien los filtros
  useEffect(() => {
    console.log('🔄 useEffect disparado - Token:', !!token, 'Page:', page, 'RowsPerPage:', rowsPerPage);
    if (token) {
      // Añadir un pequeño delay para evitar múltiples llamadas mientras el usuario escribe
      const delayTimer = setTimeout(() => {
        loadRequirements(true); // Silent para no causar saltos al filtrar
      }, 300); // 300ms de delay
      
      return () => clearTimeout(delayTimer);
    } else {
      console.warn('⚠️ No hay token disponible');
    }
  }, [page, rowsPerPage, token, specCodeFilter, searchTerm]);

  // Cargar configuraciones de traducción y versiones solo una vez al montar
  useEffect(() => {
    if (token) {
      loadTranslationConfigs();
      loadVersions();
    }
  }, [token]);

  // Auto-refresh cuando hay una importación activa
  useEffect(() => {
    let refreshInterval;
    
    // Si hay una importación en proceso, hacer polling cada 2 segundos
    if (currentProcessing && currentProcessing.type === 'import' && currentProcessing.status === 'processing') {
      console.log('🔄 Iniciando auto-refresh para importación activa');
      refreshInterval = setInterval(() => {
        console.log('⏰ Auto-refresh: recargando requisitos...');
        loadRequirements(true); // true = silent refresh
      }, 2000); // Cada 2 segundos
    }
    
    // Si la importación acaba de completarse, hacer una recarga final
    if (currentProcessing && currentProcessing.type === 'import' && currentProcessing.status === 'completed') {
      console.log('✅ Importación completada, recargando tabla final...');
      setTimeout(() => {
        loadRequirements(true); // true = silent refresh
      }, 500);
    }
    
    // Limpiar interval al desmontar o cuando cambie el estado
    return () => {
      if (refreshInterval) {
        console.log('🛑 Deteniendo auto-refresh');
        clearInterval(refreshInterval);
      }
    };
  }, [currentProcessing]);

  // Cargar conteo de requisitos pendientes de validación
  const loadPendingCount = async () => {
    try {
      const data = await getPendingCount();
      setPendingCount(data.count || 0);
    } catch (error) {
      console.warn('Could not load pending count:', error);
    }
  };

  useEffect(() => {
    loadPendingCount();
    // Refrescar conteo cada 30s
    const interval = setInterval(loadPendingCount, 30000);
    return () => clearInterval(interval);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar versiones disponibles
  const loadVersions = async () => {
    try {
      const response = await axios.get('/versions/');
      setVersions(response.data.versions || []);
    } catch (error) {
      console.error('Error loading versions:', error);
    }
  };

  // Cargar configuraciones de traducción
  const loadTranslationConfigs = async () => {
    try {
      const response = await axios.get('/translation-configs/');
      setTranslationConfigs(response.data.configs || []);
      
      // Seleccionar la configuración por defecto si existe
      const defaultConfig = response.data.configs?.find(c => c.is_default);
      if (defaultConfig && !selectedConfigId) {
        setSelectedConfigId(defaultConfig.id);
      }
    } catch (error) {
      console.error('Error loading translation configs:', error);
    }
  };

  // Función para buscar (ahora solo resetea la página, el useEffect cargará automáticamente)
  const handleSearch = () => {
    setPage(0); // Resetear a la primera página (esto dispara el useEffect)
  };

  // Función para abrir el diálogo de edición
  const handleEdit = (requirement) => {
    setCurrentRequirement(requirement);
    setEditedVersion(requirement.version_id || '');
    setEditedNameTranslation(requirement.spec_name_translated_edited || requirement.spec_name_translated || '');
    setEditedTextTranslation(requirement.spec_text_translated_edited || requirement.spec_text_translated || '');
    setIsValidated(requirement.is_validated || false);
    
    // Cargar valores SMART
    setSmartSpecific(requirement.smart_specific || 0);
    setSmartMeasurable(requirement.smart_measurable || 0);
    setSmartAchievable(requirement.smart_achievable || 0);
    setSmartRelevant(requirement.smart_relevant || 0);
    setSmartTimeBound(requirement.smart_time_bound || 0);
    setSmartOverallScore(requirement.smart_overall_score || 0);
    setSmartSpecificReasoning(requirement.smart_specific_reasoning || '');
    setSmartMeasurableReasoning(requirement.smart_measurable_reasoning || '');
    setSmartAchievableReasoning(requirement.smart_achievable_reasoning || '');
    setSmartRelevantReasoning(requirement.smart_relevant_reasoning || '');
    setSmartTimeBoundReasoning(requirement.smart_time_bound_reasoning || '');
    
    // Cargar valores de override y justificaciones
    setSmartSpecificOverride(requirement.smart_specific_override || false);
    setSmartSpecificJustification(requirement.smart_specific_justification || '');
    setSmartMeasurableOverride(requirement.smart_measurable_override || false);
    setSmartMeasurableJustification(requirement.smart_measurable_justification || '');
    setSmartAchievableOverride(requirement.smart_achievable_override || false);
    setSmartAchievableJustification(requirement.smart_achievable_justification || '');
    setSmartRelevantOverride(requirement.smart_relevant_override || false);
    setSmartRelevantJustification(requirement.smart_relevant_justification || '');
    setSmartTimeBoundOverride(requirement.smart_time_bound_override || false);
    setSmartTimeBoundJustification(requirement.smart_time_bound_justification || '');
    setSmartAnalysisCompleted(requirement.smart_analysis_completed || false);
    
    setEditDialogOpen(true);
  };

  // Función para guardar las ediciones
  const handleSave = async () => {
    // Validar SMART criteria si el análisis está completo
    if (smartAnalysisCompleted && typeof window.validateSmartAssessment === 'function') {
      const isValid = window.validateSmartAssessment();
      if (!isValid) {
        // La validación falló - el diálogo de error ya se mostró
        return;
      }
    }
    
    setSaving(true);
    setError('');
    
    try {
      const response = await axios.put(
        `/master-requirements/${currentRequirement.id}/update/`,
        {
          spec_name_translated_edited: editedNameTranslation,
          spec_text_translated_edited: editedTextTranslation,
          is_validated: isValidated,
          version_id: editedVersion || null,
          smart_specific: smartSpecific,
          smart_measurable: smartMeasurable,
          smart_achievable: smartAchievable,
          smart_relevant: smartRelevant,
          smart_time_bound: smartTimeBound,
          smart_specific_override: smartSpecificOverride,
          smart_specific_justification: smartSpecificJustification,
          smart_measurable_override: smartMeasurableOverride,
          smart_measurable_justification: smartMeasurableJustification,
          smart_achievable_override: smartAchievableOverride,
          smart_achievable_justification: smartAchievableJustification,
          smart_relevant_override: smartRelevantOverride,
          smart_relevant_justification: smartRelevantJustification,
          smart_time_bound_override: smartTimeBoundOverride,
          smart_time_bound_justification: smartTimeBoundJustification
        }
      );

      if (response.data.status === 'success') {
        setSuccess('Translation saved successfully');
        setEditDialogOpen(false);
        loadRequirements(true); // Recargar silenciosamente para mantener la posición
      }
    } catch (err) {
      setError('Error saving: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Función para abrir el diálogo de confirmación de eliminación
  const handleDelete = (requirement) => {
    setRequirementToDelete(requirement);
    setConfirmDeleteOpen(true);
  };

  // Función para confirmar y ejecutar la eliminación
  const confirmDelete = async () => {
    if (!requirementToDelete) return;

    const deletedId = requirementToDelete.id;
    setError('');
    setConfirmDeleteOpen(false);
    
    // Optimistic update: quitar el elemento de la lista inmediatamente
    setRequirements(prev => prev.filter(r => r.id !== deletedId));
    setTotalCount(prev => Math.max(0, prev - 1));
    setRequirementToDelete(null);

    try {
      const response = await axios.delete(
        `/master-requirements/${deletedId}/delete/`
      );

      if (response.data.status === 'success') {
        setSuccess('Requirement deleted successfully');
        loadRequirements(true); // Sincronizar con el servidor en background
      }
    } catch (err) {
      // Revertir el optimistic update si falló
      loadRequirements(true);
      setError('Error deleting: ' + (err.response?.data?.message || err.message));
    }
  };

  // Función para borrar todos los requerimientos
  const confirmDeleteAll = async () => {
    setDeletingAll(true);
    setConfirmDeleteAllOpen(false);
    try {
      const response = await axios.delete('/master-requirements/delete-all/');
      if (response.data.status === 'success') {
        setRequirements([]);
        setTotalCount(0);
        showSuccess(`${response.data.deleted_count} requirements deleted successfully`);
      }
    } catch (err) {
      showError('Error deleting all: ' + (err.response?.data?.message || err.message));
    } finally {
      setDeletingAll(false);
    }
  };

  // Función para manejar la importación de Excel
  const handleImportExcel = () => {
    setImportDialogOpen(true);
    setImportFile(null);
    setImportSummary(null);
    setSelectedVersionId(null);
  };

  // Función para mostrar modal de confirmación de descarga
  const handleShowDownloadConfirm = () => {
    if (totalCount === 0) {
      setError('No requirements to export');
      return;
    }
    setConfirmDownloadOpen(true);
  };

  // Función para preparar análisis SMART
  const handlePrepareSmartAnalysis = async () => {
    setError('');
    
    try {
      // Obtener un número grande de requisitos para analizar todos los validados
      // Usamos page_size grande para obtener todos los requisitos en una sola llamada
      const response = await axios.get('/master-requirements/', {
        params: {
          page: 1,
          page_size: 10000 // Suficientemente grande para obtener todos
        },
        timeout: 120000
      });

      if (response.data.status === 'success') {
        const allRequirements = response.data.data?.requirements || [];
        
        // Filtrar solo los validados
        const validatedReqs = allRequirements.filter(req => req.is_validated);
        
        if (validatedReqs.length === 0) {
          setError('No validated requirements found. Please validate requirements first.');
          return;
        }

        // Contar cuántos ya están analizados
        // Verificar tanto smart_analysis_completed como que tenga un score válido
        const alreadyAnalyzed = validatedReqs.filter(req => 
          req.smart_analysis_completed === true && 
          req.smart_overall_score != null && 
          req.smart_overall_score > 0
        );
        const toAnalyze = validatedReqs.length - alreadyAnalyzed.length;

        console.log('📊 SMART Analysis Check:', {
          totalValidated: validatedReqs.length,
          alreadyAnalyzed: alreadyAnalyzed.length,
          toAnalyze: toAnalyze,
          requirements: validatedReqs.map(req => ({
            code: req.spec_code,
            smart_analysis_completed: req.smart_analysis_completed,
            smart_overall_score: req.smart_overall_score
          }))
        });

        if (toAnalyze === 0) {
          setSuccess(`✅ All ${validatedReqs.length} validated requirement(s) are already analyzed! No further action needed.`);
          return;
        }

        setSmartAnalysisInfo({
          total_requirements: validatedReqs.length,
          already_analyzed: alreadyAnalyzed.length,
          to_analyze: toAnalyze
        });
        setConfirmSmartAnalysisOpen(true);
      }
    } catch (err) {
      console.error('Error preparing SMART analysis:', err);
      setError('Error preparing SMART analysis: ' + (err.response?.data?.message || err.message));
    }
  };

  // Función para confirmar y ejecutar análisis SMART
  const confirmSmartAnalysis = async () => {
    setConfirmSmartAnalysisOpen(false);
    setAnalyzingSmart(true);
    setError('');
    
    try {
      // Iniciar el análisis asíncrono
      const response = await axios.post('/master-requirements/analyze-smart/', {
        validated_only: true,
        batch_size: 5
      });

      if (response.data.status === 'started') {
        // Tarea iniciada - obtener task_id y comenzar polling
        const taskId = response.data.task_id;
        const totalPending = response.data.data?.total_pending || 0;
        
        setSuccess(`SMART analysis started for ${totalPending} requirement(s). Processing in background...`);
        
        // Polling del estado de la tarea
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await axios.get(`master-requirements/smart-task-status/${taskId}/`);
            const taskData = statusResponse.data;
            
            if (taskData.status === 'SUCCESS') {
              clearInterval(pollInterval);
              setAnalyzingSmart(false);
              
              const result = taskData.result || {};
              const analyzed = result.successful || 0;
              
              if (analyzed > 0) {
                setSuccess(`SMART analysis completed! ${analyzed} requirement(s) analyzed successfully.`);
              } else {
                setSuccess(result.message || 'Analysis completed');
              }
              
              // Recargar requisitos
              loadRequirements(true);
            } else if (taskData.status === 'FAILURE') {
              clearInterval(pollInterval);
              setAnalyzingSmart(false);
              setError(`Analysis failed: ${taskData.error || 'Unknown error'}`);
            } else if (taskData.status === 'PROGRESS') {
              // Actualizar mensaje de progreso
              const progress = taskData.progress || 0;
              const current = taskData.current || 0;
              const total = taskData.total || 0;
              setSuccess(`Processing: ${current}/${total} requirements (${progress}%)`);
            }
          } catch (pollError) {
            console.error('Error polling task status:', pollError);
          }
        }, 2000); // Poll cada 2 segundos
        
        // Cancelar polling después de 5 minutos
        setTimeout(() => {
          clearInterval(pollInterval);
          if (analyzingSmart) {
            setAnalyzingSmart(false);
            setError('Analysis timeout - please check task status manually');
          }
        }, 300000);
        
      } else if (response.data.status === 'completed') {
        // Ya estaba todo completo
        const analyzed = response.data.data?.analyzed || 0;
        if (analyzed > 0) {
          setSuccess(`SMART analysis completed! ${analyzed} requirement(s) analyzed successfully.`);
        } else {
          setSuccess(response.data.message || 'Analysis completed');
        }
        loadRequirements(true);
        setAnalyzingSmart(false);
      }
    } catch (err) {
      setError('Error starting SMART analysis: ' + (err.response?.data?.message || err.message));
      setAnalyzingSmart(false);
    }
  };

  // Función para exportar todos los requisitos
  const handleExportAll = async () => {
    setConfirmDownloadOpen(false);
    setExporting(true);
    setError('');
    
    try {
      const response = await axios.get('/master-requirements/export-all/', {
        responseType: 'blob',
        timeout: 120000
      });

      // Crear enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Obtener nombre del archivo desde los headers
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'requisitos_maestros.xlsx';
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      }
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('All requirements exported successfully');
    } catch (err) {
      setError('Error exporting: ' + (err.response?.data?.message || err.message));
    } finally {
      setExporting(false);
    }
  };

  // Función para procesar la importación
  const handleUploadExcel = async () => {
    if (!importFile) {
      setError('Please select an Excel file');
      return;
    }

    setImporting(true);
    setError('');
    setImportSummary(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('sheet_name', 'prueba');
      
      // Añadir configuración de traducción si está seleccionada
      if (selectedConfigId) {
        formData.append('translation_config_id', selectedConfigId);
      }

      // Añadir versión si está seleccionada
      if (selectedVersionId) {
        formData.append('version_id', selectedVersionId);
      }

      const response = await axios.post(
        '/master-requirements/import-excel/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 120000 // 2 minutos para uploads grandes
        }
      );

      if (response.data.status === 'success') {
        // Nueva respuesta async - solo task_id, no summary inmediato
        if (response.data.task_id) {
          setSuccess(`Import started successfully. Processing ${importFile.name}...`);
          
          // Cerrar el diálogo de importación
          setImportDialogOpen(false);
          setImportFile(null);
          setImportSummary(null);
          
          // Abrir el panel lateral para ver el progreso
          if (setProcessingPanelOpen) {
            setProcessingPanelOpen(true);
          }
          
          // No necesitamos setTimeout aquí - el auto-refresh se encargará
          // El useEffect detectará la importación activa y comenzará el polling automático
        } 
        // Respuesta sync antigua (por compatibilidad)
        else if (response.data.summary) {
          setImportSummary(response.data.summary);
          setSuccess(`Import successful: ${response.data.summary.created} created, ${response.data.summary.updated} updated`);
          
          // Cerrar el diálogo de importación
          setImportDialogOpen(false);
          setImportFile(null);
          
          // Recargar la lista de requisitos
          setTimeout(() => {
            loadRequirements(true); // Silenciosa para no causar saltos
          }, 1000);
        }
      }
    } catch (err) {
      setError('Error importing: ' + (err.response?.data?.message || err.message));
    } finally {
      setImporting(false);
    }
  };

  // Función para validar master requirements
  const handleValidateMasterRequirements = async (requirementIds = null) => {
    setError('');
    
    try {
      const payload = requirementIds 
        ? { requirement_ids: requirementIds }
        : { validate_all: true };
      
      const response = await axios.post(
        '/master-requirements/validate/',
        payload
      );

      if (response.data.status === 'success') {
        setSuccess(`${response.data.validated_count} requirement(s) validated successfully`);
        loadRequirements(true); // Recargar silenciosamente
      }
    } catch (err) {
      setError('Error validating: ' + (err.response?.data?.message || err.message));
    }
  };

  // Función para resetear todas las validaciones
  const handleResetAllValidations = async () => {
    if (!window.confirm('Are you sure you want to mark ALL requirements as NOT VALIDATED? This cannot be undone.')) {
      return;
    }

    setError('');
    
    try {
      const response = await axios.post('/master-requirements/reset-validations/');

      if (response.data.status === 'success') {
        setSuccess(`${response.data.count} requirement(s) marked as not validated`);
        loadRequirements(true); // Recargar silenciosamente
      }
    } catch (err) {
      setError('Error resetting validations: ' + (err.response?.data?.message || err.message));
    }
  };

  // Función para obtener el chip de estado
  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', color: 'warning' },
      translating: { label: 'Translating', color: 'info' },
      completed: { label: 'Completed', color: 'success' },
      failed: { label: 'Error', color: 'error' }
    };

    const config = statusConfig[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Sub-tabs: Master Requirements | Pending Validation */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={activeSubTab}
          onChange={(e, v) => setActiveSubTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Validated Requirements" />
          <Tab
            label={
              <Badge badgeContent={pendingCount} color="warning" max={99}>
                <Box sx={{ pr: pendingCount > 0 ? 2 : 0 }}>Pending Validation</Box>
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {activeSubTab === 0 && (
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            Master Requirements Management
            <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
              {userPermissions?.can_edit && (
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={analyzingSmart ? <CircularProgress size={20} /> : <PsychologyIcon />}
                  onClick={handlePrepareSmartAnalysis}
                  disabled={analyzingSmart || totalCount === 0}
                >
                  {analyzingSmart ? 'Analyzing...' : 'Analyze SMART'}
                </Button>
              )}
              <Button
                variant="outlined"
                color="success"
                startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleShowDownloadConfirm}
                disabled={exporting || totalCount === 0}
              >
                {exporting ? 'Exporting...' : 'Export All'}
              </Button>
              {userPermissions?.can_create && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleImportExcel}
                >
                  Import PDF
                </Button>
              )}
              {userPermissions?.can_delete && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={deletingAll ? <CircularProgress size={20} /> : <DeleteIcon />}
                  onClick={() => setConfirmDeleteAllOpen(true)}
                  disabled={deletingAll || totalCount === 0}
                >
                  Delete All
                </Button>
              )}
            </Box>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {/* Barra de búsqueda y filtros */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Filter by Code"
                value={specCodeFilter}
                onChange={(e) => setSpecCodeFilter(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  endAdornment: <SearchIcon />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search in all fields"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                fullWidth
              >
                Search
              </Button>
              <Tooltip title="Reload">
                <IconButton onClick={() => loadRequirements(false)} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>

          {/* Tabla de requerimientos */}
          {loading && requirements.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Code</strong></TableCell>
                      <TableCell><strong>Version</strong></TableCell>
                      <TableCell><strong>Translation</strong></TableCell>
                      <TableCell><strong>SMART Score</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Created By</strong></TableCell>
                      <TableCell><strong>Last Edited By</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requirements.map((req) => (
                      <TableRow key={req.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {req.spec_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {req.version || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              maxWidth: 250,
                              fontStyle: req.spec_name_translated_edited ? 'normal' : 'italic',
                              color: req.spec_name_translated_edited ? 'success.main' : 'text.secondary'
                            }}
                          >
                            {(() => { const t = req.spec_name_translated_edited || req.spec_name_translated || 'No translation'; return t.length > 40 ? t.substring(0, 40) + '...' : t; })()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {req.smart_analysis_completed ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {(req.smart_overall_score * 100).toFixed(1)}%
                              </Typography>
                              <Chip
                                label={req.is_smart_compliant ? 'Compliant' : 'Non-compliant'}
                                color={req.is_smart_compliant ? 'success' : 'warning'}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Not analyzed
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{getStatusChip(req.translation_status)}</TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            {req.created_by || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {req.created_at ? new Date(req.created_at).toLocaleDateString() : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {req.last_edited_by ? (
                            <>
                              <Typography variant="caption" display="block">
                                {req.last_edited_by}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {new Date(req.last_edited_at).toLocaleDateString()}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Not edited
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={userPermissions?.can_edit ? "Edit translations" : "View details (read-only)"}>
                            <IconButton
                              color={userPermissions?.can_edit ? "primary" : "default"}
                              onClick={() => handleEdit(req)}
                              size="small"
                            >
                              {userPermissions?.can_edit ? <EditIcon /> : <Visibility />}
                            </IconButton>
                          </Tooltip>
                          {userPermissions?.can_delete && (
                            <Tooltip title="Delete requirement">
                              <IconButton
                                color="error"
                                onClick={() => handleDelete(req)}
                                size="small"
                                sx={{ ml: 1 }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[50, 100, 200]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              />
            </>
          )}
        </CardContent>
      </Card>
      )}

      {activeSubTab === 1 && (
        <PendingRequirementsPanel
          userPermissions={userPermissions}
          showSuccess={showSuccess}
          showError={showError}
          onRefreshCount={loadPendingCount}
        />
      )}

      {/* Diálogo de edición */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {userPermissions?.can_edit ? 'Edit' : 'View'} Translations & SMART Assessment - {currentRequirement?.spec_code}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Versión */}
            <FormControl fullWidth sx={{ mb: 3 }} disabled={!userPermissions?.can_edit}>
              <InputLabel>Version</InputLabel>
              <Select
                value={editedVersion}
                label="Version"
                onChange={(e) => setEditedVersion(e.target.value)}
              >
                <MenuItem value=""><em>— No version —</em></MenuItem>
                {versions.map((v) => (
                  <MenuItem key={v.id} value={v.id}>{v.version}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Nombre Original */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Original Name (Dutch):
            </Typography>
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
              <Typography variant="body2">{currentRequirement?.spec_name}</Typography>
            </Paper>

            {/* Campo Editable del Nombre */}
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Edited Name Translation"
              value={editedNameTranslation}
              onChange={(e) => setEditedNameTranslation(e.target.value)}
              sx={{ mb: 4 }}
              helperText="Enter the translation for the requirement name"
              disabled={!userPermissions?.can_edit}
              InputProps={{
                readOnly: !userPermissions?.can_edit
              }}
            />

            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', my: 3 }} />

            {/* Texto Original */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Original Text (Dutch):
            </Typography>
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default', maxHeight: 150, overflow: 'auto' }}>
              <Typography variant="body2">{currentRequirement?.spec_text}</Typography>
            </Paper>

            {/* Campo Editable del Texto */}
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Edited Text Translation"
              value={editedTextTranslation}
              onChange={(e) => setEditedTextTranslation(e.target.value)}
              helperText="Enter the translation for the requirement text"
              disabled={!userPermissions?.can_edit}
              InputProps={{
                readOnly: !userPermissions?.can_edit
              }}
            />

            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', my: 3 }} />

            {/* Modern SMART Criteria Assessment - Solo mostrar si está completado */}
            {smartAnalysisCompleted && (
              <SmartAssessmentCompact
              smartSpecific={smartSpecific}
              smartMeasurable={smartMeasurable}
              smartAchievable={smartAchievable}
              smartRelevant={smartRelevant}
              smartTimeBound={smartTimeBound}
              smartSpecificReasoning={smartSpecificReasoning}
              smartMeasurableReasoning={smartMeasurableReasoning}
              smartAchievableReasoning={smartAchievableReasoning}
              smartRelevantReasoning={smartRelevantReasoning}
              smartTimeBoundReasoning={smartTimeBoundReasoning}
              smartSpecificOverride={smartSpecificOverride}
              smartSpecificJustification={smartSpecificJustification}
              smartMeasurableOverride={smartMeasurableOverride}
              smartMeasurableJustification={smartMeasurableJustification}
              smartAchievableOverride={smartAchievableOverride}
              smartAchievableJustification={smartAchievableJustification}
              smartRelevantOverride={smartRelevantOverride}
              smartRelevantJustification={smartRelevantJustification}
              smartTimeBoundOverride={smartTimeBoundOverride}
              smartTimeBoundJustification={smartTimeBoundJustification}
              onSpecificChange={setSmartSpecific}
              onMeasurableChange={setSmartMeasurable}
              onAchievableChange={setSmartAchievable}
              onRelevantChange={setSmartRelevant}
              onTimeBoundChange={setSmartTimeBound}
              onSpecificOverrideChange={setSmartSpecificOverride}
              onSpecificJustificationChange={setSmartSpecificJustification}
              onMeasurableOverrideChange={setSmartMeasurableOverride}
              onMeasurableJustificationChange={setSmartMeasurableJustification}
              onAchievableOverrideChange={setSmartAchievableOverride}
              onAchievableJustificationChange={setSmartAchievableJustification}
              onRelevantOverrideChange={setSmartRelevantOverride}
              onRelevantJustificationChange={setSmartRelevantJustification}
              onTimeBoundOverrideChange={setSmartTimeBoundOverride}
              onTimeBoundJustificationChange={setSmartTimeBoundJustification}
              readOnly={!userPermissions?.can_edit}
              />
            )}

            {!smartAnalysisCompleted && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  SMART Criteria Assessment not available. Run SMART analysis to view assessment.
                </Typography>
              </Alert>
            )}

            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', my: 3 }} />

            {/* Checkbox de Validación */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isValidated}
                  onChange={(e) => setIsValidated(e.target.checked)}
                  color="success"
                  disabled={!userPermissions?.can_edit}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Validate this requirement
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Mark as validated to include in exports
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditDialogOpen(false)}
            startIcon={<CancelIcon />}
            disabled={saving}
          >
            {userPermissions?.can_edit ? 'Cancel' : 'Close'}
          </Button>
          {userPermissions?.can_edit && (
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Diálogo de importación de Excel */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          📥 Import Requirements from Excel
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              The Excel file must contain the columns: <strong>specCode</strong>, <strong>specName</strong> and <strong>specText</strong> in the "prueba" sheet.
            </Alert>

            {/* Selector de configuración de traducción */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Translation Configuration
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => setShowConfigManager(true)}
                  title="Manage configurations"
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <FormControl fullWidth size="small">
                <InputLabel>Select configuration</InputLabel>
                <Select
                  value={selectedConfigId || ''}
                  onChange={(e) => setSelectedConfigId(e.target.value)}
                  label="Select configuration"
                >
                  <MenuItem value="">
                    <em>No configuration (default)</em>
                  </MenuItem>
                  {translationConfigs.map((config) => (
                    <MenuItem key={config.id} value={config.id}>
                      {config.name} → {config.target_language}
                      {config.is_default && ' (default)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {selectedConfigId && (
                <Paper sx={{ p: 1.5, mt: 1, bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                  {(() => {
                    const selectedConfig = translationConfigs.find(c => c.id === selectedConfigId);
                    if (!selectedConfig) return null;
                    
                    return (
                      <>
                        {selectedConfig.domain && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                            <strong>Domain:</strong> {selectedConfig.domain}
                          </Typography>
                        )}
                        {selectedConfig.context && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                            <strong>Context:</strong>{' '}
                            {selectedConfig.context.length > 100
                              ? selectedConfig.context.substring(0, 100) + '...'
                              : selectedConfig.context}
                          </Typography>
                        )}
                        {selectedConfig.glossaries && selectedConfig.glossaries.length > 0 && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              Glossaries used:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selectedConfig.glossaries.map((glossary) => (
                                <Chip 
                                  key={glossary.id}
                                  label={glossary.name}
                                  size="small"
                                  color={glossary.is_active ? "primary" : "default"}
                                  sx={{ fontSize: '0.7rem', height: '20px' }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </>
                    );
                  })()}
                </Paper>
              )}
            </Box>

            {/* Selector de versión */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Requirements Version
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Select version</InputLabel>
                <Select
                  value={selectedVersionId || ''}
                  onChange={(e) => setSelectedVersionId(e.target.value)}
                  label="Select version"
                >
                  <MenuItem value="">
                    <em>No version assigned</em>
                  </MenuItem>
                  {versions.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.version}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <input
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              id="excel-file-upload"
              type="file"
              onChange={(e) => setImportFile(e.target.files[0])}
            />
            <label htmlFor="excel-file-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2 }}
              >
                Select Excel file
              </Button>
            </label>

            {importFile && (
              <Alert severity="success" sx={{ mb: 2 }}>
                File selected: <strong>{importFile.name}</strong>
              </Alert>
            )}

            {importSummary && (
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Import Summary:
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">Total rows: {importSummary.total_rows}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="success.main">Created: {importSummary.created}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="info.main">Updated: {importSummary.updated}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="warning.main">Skipped: {importSummary.skipped}</Typography>
                  </Grid>
                  {importSummary.errors > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="error.main">Errors: {importSummary.errors}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setImportDialogOpen(false)}
            disabled={importing}
          >
            {importSummary ? 'Close' : 'Cancel'}
          </Button>
          {!importSummary && (
            <Button
              onClick={handleUploadExcel}
              variant="contained"
              startIcon={importing ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              disabled={importing || !importFile}
            >
              {importing ? 'Importing...' : 'Import'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación de descarga */}
      <Dialog
        open={confirmDownloadOpen}
        onClose={() => setConfirmDownloadOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DownloadIcon color="primary" />
            Confirm Requirements Download
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              All requirements will be exported to Excel
            </Alert>
            <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Total requirements to export:
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#212121', fontWeight: 'bold', textAlign: 'center', mt: 1 }}>
                    {totalCount}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Validated:
                  </Typography>
                  <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 'bold', textAlign: 'center' }}>
                    {validatedCount}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Not validated:
                  </Typography>
                  <Typography variant="h5" sx={{ color: 'warning.main', fontWeight: 'bold', textAlign: 'center' }}>
                    {totalCount - validatedCount}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            <Typography variant="body1" sx={{ mt: 3, textAlign: 'center' }}>
              Do you want to continue with the download?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setConfirmDownloadOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleExportAll}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              bgcolor: '#212121',
              color: '#fff',
              '&:hover': { bgcolor: '#424242' }
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación de eliminación */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            Confirm Requirement Deletion
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              ⚠️ This action cannot be undone
            </Alert>
            {requirementToDelete && (
              <Paper sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  You are about to delete:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'error.main', mb: 1 }}>
                  {requirementToDelete.spec_code}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {requirementToDelete.spec_name}
                </Typography>
              </Paper>
            )}
            <Typography variant="body1" sx={{ mt: 3, textAlign: 'center' }}>
              Are you sure you want to delete this requirement?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setConfirmDeleteOpen(false);
              setRequirementToDelete(null);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación de análisis SMART */}
      <Dialog
        open={confirmSmartAnalysisOpen}
        onClose={() => setConfirmSmartAnalysisOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PsychologyIcon color="info" />
            Confirm SMART Analysis
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
             This will analyze all validated requirements using SMART criteria
            </Alert>
            
            {smartAnalysisInfo && (
              <Paper sx={{ p: 3, bgcolor: 'rgba(0,0,0,0.02)' }}>
                <Typography variant="h6" gutterBottom>
                  Analysis Overview
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      Total Requirements:
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold', textAlign: 'center' }}>
                      {smartAnalysisInfo.total_requirements}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      Already Analyzed:
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 'bold', textAlign: 'center' }}>
                      {smartAnalysisInfo.already_analyzed}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ bgcolor: 'info.dark', p: 2, borderRadius: 1 }}>
                      <Typography variant="body2" color="white" align="center" gutterBottom>
                        To be analyzed:
                      </Typography>
                      <Typography variant="h4" sx={{ color: 'info.light', fontWeight: 'bold', textAlign: 'center' }}>
                        {smartAnalysisInfo.to_analyze}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}
            
            <Typography variant="body1" sx={{ mt: 3, textAlign: 'center' }}>
              Do you want to start the SMART analysis?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              This may take a few moments depending on the number of requirements.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setConfirmSmartAnalysisOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmSmartAnalysis}
            variant="contained"
            color="info"
            startIcon={<PsychologyIcon />}
          >
            Start Analysis
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para borrar todos */}
      <Dialog
        open={confirmDeleteAllOpen}
        onClose={() => setConfirmDeleteAllOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            Delete All Requirements
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              ⚠️ This action is irreversible. All {totalCount} requirements will be permanently deleted.
            </Alert>
            <Typography variant="body1" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
              Are you absolutely sure you want to delete ALL requirements?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDeleteAllOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteAll}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Translation Config Manager */}
      <TranslationConfigManager
        open={showConfigManager}
        onClose={() => setShowConfigManager(false)}
        onConfigCreated={() => {
          loadTranslationConfigs();
        }}
      />
    </Box>
  );
};

export default MasterRequirementsManager;
